import { get, first, chain, size, find, reduce, replace, orderBy, isNumber, filter, pick, map, omit, isEmpty, findLast } from "lodash"
import pluralize from "pluralize"
import { Info } from "@mui/icons-material"
import Tooltip from "components/material/tooltip"
import { makeCurrencyFormatter, numberFormatter } from "helpers/formatters/numbers"
import { getRollupSummaryLabel } from "helpers/rollups"
import { createSelector } from "reselect"
import { VIEW_TYPES } from "helpers/base"
import { sanitizeBasketTicker } from "helpers/swaps"

const EMPTY_ARRAY = []
const EMPTY_OBJECT = {}

function getGroupSummaryText(groups) {
  const numberOfSecurities = get(first(groups), "contributors.length", 0)
  // [TODO] [#184018795] JSX shouldn't be in selectors or models
  return <span className="summary-text">
    {numberOfSecurities} {pluralize("Security", numberOfSecurities)}
  </span>
}

function getBasketSummaryText(basketContributors) {
  const numberOfBasketContributors = size(basketContributors)
  return (
    <span className="summary-text">
      {numberOfBasketContributors} {pluralize("Security", numberOfBasketContributors)}
    </span>
  )
}

function getContributorsGroupSummaryText({ groups, contributorsGrouping, gmvLabel }) {
  return (
    <span className="summary-text">
      {groups.length} {pluralize(getRollupSummaryLabel(contributorsGrouping), groups.length)}{gmvLabel}
    </span>
  )
}

export function contributorsSummaryTextElem({
  contributorsGrouping,
  contributors,
  groups,
  selectedGroup,
  selectedBasket,
  basketContributors,
  positionsDisparity,
  referenceEquity,
  gmv,
  currency,
}) {
  const dollarFormatter = makeCurrencyFormatter(currency)
  let gmvLabel
  if (referenceEquity) {
    gmvLabel = <span>
      {` | ${dollarFormatter(referenceEquity)}  Ref equity`}
      <Tooltip placement="right" title={`${dollarFormatter(gmv)}  GMV`}>
        <Info className="summary-gmv-icon" />
      </Tooltip>
    </span>
  } else if (gmv) {
    gmvLabel = ` | ${dollarFormatter(gmv)}  GMV`
  }

  if (selectedGroup) return getGroupSummaryText(groups)
  if (contributorsGrouping) return getContributorsGroupSummaryText({ groups, contributorsGrouping, gmvLabel })
  if (selectedBasket) return getBasketSummaryText(basketContributors)

  const disparityLabel = positionsDisparity ? "Unique " : ""

  return (
    <span className="summary-text">
      {contributors.length} {disparityLabel}{pluralize("Security", contributors.length)}{gmvLabel}
    </span>
  )
}

export function positionSetContributorsSummaryText(contributorsGrouping, contributors, groups, selectedGroup, showNotHeld, viewType, positionSetType = "portfolio") {
  if (contributorsGrouping && !selectedGroup) {
    return `${groups.length} ${pluralize(getRollupSummaryLabel(contributorsGrouping), groups.length)}`
  }

  let contributorList = contributors
  if (selectedGroup) {
    contributorList = get(first(groups), "contributors")
  }

  let numberOfSecurities = size(contributorList)
  if (viewType === VIEW_TYPES.ACTIVE && !showNotHeld) {
    numberOfSecurities = chain(contributorList)
      .filter(item => item.id !== "NOT HELD")
      .size()
      .value()
  }
  const middlePart = showNotHeld && viewType === VIEW_TYPES.ACTIVE ? "active" : `${positionSetType}${viewType === VIEW_TYPES.ACTIVE ? " active" : ""}`
  return `${numberOfSecurities} ${middlePart} ${pluralize("contributor", numberOfSecurities)}`
}

const getRawSelectedGroup = (_, props) => get(props, "selectedGroup", EMPTY_OBJECT)

export function makeContributorsGroupDropdownSelector(columns, getSelectedGroup = getRawSelectedGroup) {
  const getSelectedCategory = (_, props) => get(props, "selectedOption.label")
  const getSelected = (_, props) => get(props, "selected")
  const getContributorsGrouping = (_, props) => get(props, "contributorsGrouping")

  return createSelector(
    [getSelected, getSelectedCategory, getSelectedGroup, getContributorsGrouping],
    (selected, selectedCategory, selectedGroup, contributorsGrouping) => {
      const { group, details } = columns
      // Replace element 0 with a new object rather than set([...arr], "0.label", …): the spread
      // copies the array but keeps the same element refs, so set() would mutate the shared
      // (module-level) column-config object in place.
      if (selectedGroup) {
        return [{ ...group[0], label: get(contributorsGrouping, "label") }, ...group.slice(1)]
      }
      if (selected) {
        return [{ ...details[0], label: selectedCategory }, ...details.slice(1)]
      }

      return group
    },
  )
}

/**
 * Assigns attributes from second contributor into the first. Mutates the first contributor.
 */
export function combineContributors(first, second) {
  if (first.assetClass === "swap") {
    first.id = sanitizeBasketTicker(second.id)
    first.economicExposure += Math.abs(second.economicExposure)
    first.description = first.description ?? second.description
  } else {
    first.economicExposure += second.economicExposure
  }
}

// Only the composition screen combines split unmodeled baskets but this is also used by asset view
// hence the combineSplitUnmodeledBaskets parameter
export function compareIdentifier(position1, position2, combineSplitUnmodeledBaskets = false) {
  let pos1Id = position1.id
  let pos2Id = position2.id
  if (combineSplitUnmodeledBaskets) {
    pos1Id = sanitizeBasketTicker(position1.id)
    pos2Id = sanitizeBasketTicker(position2.id)
  }

  return position2.modelProviderId
    ? position1.modelProviderId === position2.modelProviderId
    // Both modelProviderId should be undefined then
    : position1.modelProviderId === position2.modelProviderId && pos1Id === pos2Id && position1.assetClass === position2.assetClass
}

export function findItemByIdentifier(collection, item, combineSplitUnmodeledBaskets) {
  return find(collection, value => compareIdentifier(value, item, combineSplitUnmodeledBaskets))
}

export function combineDuplicateContributors(positions, combineSplitUnmodeledBaskets = false) {
  return reduce(positions, (acc, position) => {
    const duplicateItem = findItemByIdentifier(acc, position, combineSplitUnmodeledBaskets)
    if (!duplicateItem) {
      acc.push(position)
    } else {
      combineContributors(duplicateItem, position)
    }
    return acc
  }, [])
}

export function combinePositionsIncludingSplitBasket(assets) {
  if (!assets) {
    return undefined
  }
  return combineDuplicateContributors(assets, true)
}

export function sortContributors(contributors, selectedField, order) {
  return orderBy(contributors, [
    (security) => {
      switch (order.by) {
        case "name":
          return security.id
        case "equity":
          return order.abs ? Math.abs(security.size) : security.size
        default: {
          const value = get(security, selectedField)
          return order.abs && isNumber(value) ? Math.abs(value) : value
        }
      }
    },
  ], [order.dir])
}

export function formatBetaInstrumentTooltip(betaInstrument) {
  if (!betaInstrument) {
    return null
  }

  // The keys below are for styling the tooltip
  // dynamicBetaId = bold
  // dynamicBetaDescription = regular, smaller font
  const { name, description, id, ticker, mic } = betaInstrument
  switch (get(betaInstrument, "type")) {
    case "PORTFOLIO":
    case "INDEX":
      return {
        dynamicBetaId: name,
        dynamicBetaDescription: description,
      }
    case "SWAP":
      // Swaps don't have a name
      return {
        dynamicBetaId: id,
        dynamicBetaDescription: description,
      }
    case "ETF":
      return {
        dynamicBetaId: ticker && mic && `${ticker}.${mic}`,
        dynamicBetaDescription: name,
      }
    case "BENCHMARK":
      return {
        dynamicBetaId: id,
        dynamicBetaDescription: name,
      }
  }
  return betaInstrument
}

export const buildContributorKey = ({ modelProviderId, assetClass, id, identifier }) => {
  let key
  if (modelProviderId) {
    key = modelProviderId
  } else if (assetClass) {
    key = `${assetClass}_${id || identifier}`
  } else {
    key = id || identifier
  }
  return replace(key, /\./g, "_")
}

// Keys for matching contributor rows to highlights (e.g. trade rows). No-longer-held contributors
// may carry a stale modelProviderId; buildContributorKey uses that first, so we also try a second
// key from composition identifier/mic. Index trades use i.{id} like useHighlightedTradeRowIds.
export function buildContributorHighlightMatchKeys(contributor) {
  const primary = buildContributorKey(contributor)
  const composition = contributor?.values?.composition || contributor?.values?.["composition-rebalanced"]
  const identifier = composition?.identifier
  const mic = composition?.mic
  if (!identifier || !mic) {
    return [primary]
  }
  const assetClassForSecondary = composition?.assetClass ?? contributor?.assetClass
  const compositionId = `${identifier}.${mic}`
  let secondary
  if (assetClassForSecondary === "index") {
    secondary = buildContributorKey({ modelProviderId: `i.${compositionId}` })
  } else {
    secondary = buildContributorKey({
      assetClass: assetClassForSecondary,
      id: compositionId,
    })
  }
  return secondary === primary ? [primary] : [primary, secondary]
}

export function computeSelected(selected, items) {
  if (!selected) {
    return selected
  }

  const found = find(items, d => d.id === selected.id)
  if (found) {
    return selected
  } else {
    return null
  }
}

export function computeSelectedValue(contributors, selected) {
  if (!selected) {
    return 0
  }

  const item = find(contributors, d => d.id === selected.id)
  if (item) {
    return item.value
  } else {
    return 0
  }
}

export function computeSortedContributors(data, selectedField, order) {
  if (data.length === 0) return EMPTY_ARRAY

  return orderBy(data, [
    (security) => {
      switch (order.by) {
        case "name":
          return security.id
        case "equity":
          return order.abs ? Math.abs(security.size) : security.size
        default: {
          const value = get(security, selectedField)
          return order.abs && isNumber(value) ? Math.abs(value) : value
        }
      }
    },
  ], [order.dir])
}

export function computeSelectedGroupValue(selectedGroup, groupedContributors, selectedField) {
  if (selectedGroup) {
    const group = find(groupedContributors, { id: selectedGroup.id })
    return get(group, selectedField)
  }

  return null
}

export function computeSelectedGroupSiblings(selectedGroup, groupedContributors, selectedField) {
  if (selectedGroup) {
    return chain(groupedContributors)
      .map(group => ({
        ...group,
        value: get(group, selectedField),
      }))
      .orderBy(["name"], ["asc"])
      .value()
  }

  return null
}

export function computeFilteredGroupedContributors(data, selectedField, selectedGroup) {
  return chain(data)
    .filter(group => !selectedGroup || selectedGroup.id === group.id)
    .map(group => ({
      ...group,
      contributors: filter(group.contributors, security => get(security, selectedField)),
    }))
    .value()
}

export function computeSortedGroupContributors(data, selectedField, order) {
  if (data.length === 0) return EMPTY_ARRAY

  return chain(data)
    .map(group => ({
      ...group,
      value: get(group, selectedField),
      contributors: orderBy(group.contributors, [
        (security) => {
          switch (order.by) {
            case "name":
              return security.id
            case "equity":
              return order.abs ? Math.abs(security.size) : security.size
            default: {
              const value = get(security, selectedField)
              return order.abs && isNumber(value) ? Math.abs(value) : value
            }
          }
        },
      ], [order.dir]),
    }))
    .orderBy([
      (group) => {
        switch (order.by) {
          case "name":
            return group.name
          case "equity":
            return order.abs ? Math.abs(group.totalPercentEquity) : group.totalPercentEquity
          default:
            return order.abs ? Math.abs(group.value) : group.value
        }
      },
    ], [order.dir])
    .value()
}

export function computeContributorFormatter({ formatter, noPercent }) {
  // If a formatter is passed in, use it
  if (formatter) { return formatter }
  // Otherwise check if the value needs to be multiplied by 100 or not
  if (noPercent) {
    return numberFormatter
  }
  return value => numberFormatter(value * 100)
}

export function extractContributorRisk(contributor) {
  return {
    ...pick(contributor, ["id", "assetClass", "modelProviderId", "description", "notHeld"]),
    risk: {
      total: get(contributor, ["attribution", "summary", "factors"]) + get(contributor, ["attribution", "summary", "specific"]),
      ...get(contributor, ["attribution", "summary"]),
    },
  }
}

/**
 * Maps nested return cumulative % fields to top level fields for performance contributors
 */
export function mapPerformanceContributorsReturnsToValues(contributors, performanceMetricField = "cumulativePercent") {
  return map(contributors, contributor => ({
    ...contributor,
    total: get(contributor, ["totalReturn", performanceMetricField]),
    attribution: {
      ...contributor.attribution,
      factors: mapPerformanceFactorsReturnsToValue(contributor.attribution.factors, performanceMetricField),
    },
  }))
}

export function mapPerformanceFactorsReturnsToValue(factors, performanceMetricField = "cumulativePercent") {
  return map(factors, factor => ({
    ...omit(factor, "return"),
    value: get(factor, ["return", performanceMetricField]),
  }))
}

export function mapPerformanceAttributionFactorReturnsToValues(attributionDates, performanceMetricField) {
  return map(attributionDates, attributionDate => ({
    ...attributionDate,
    attribution: {
      ...attributionDate.attribution,
      factors: mapPerformanceFactorsReturnsToValue(attributionDate.attribution.factors, performanceMetricField),
    },
  }))
}

export function getLastContributor(dailyContributors, date) {
  return findLast(dailyContributors, dayContributors => dayContributors?.date?.format("YYYY-MM-DD") <= date) || null
}

export function getBetaInstrumentLabelAndTooltip(betaInstrument) {
  if (!betaInstrument.type) {
    // We shouldn't reach this point, but this is to avoid an empty label if something goes wrong
    return {
      label: "Custom",
    }
  }
  const formattedBetaInstrumentTooltip = formatBetaInstrumentTooltip(betaInstrument)
  const descriptor = formattedBetaInstrumentTooltip.dynamicBetaId ? ` - ${formattedBetaInstrumentTooltip.dynamicBetaId}` : ""
  return {
    label: `Custom${descriptor}`,
    tooltipContents: formattedBetaInstrumentTooltip,
  }
}

/**
 * Creates a filter function for performance contributors based on selected field.
 * When selectedField is "total", includes contributors with any attribution value.
 * Otherwise, filters by the specific field value.
 * @param {string} selectedField - The field to filter by ("total" or a specific field name)
 * @returns {function} Filter function that takes a security and returns truthy/falsy
 */
export function createContributorFilter(selectedField) {
  if (selectedField === "total") {
    return security => security.alpha || security.factorsTotal || security.trading || security.residual
  }
  return security => get(security, selectedField)
}

export function collectLeafModelProviderIds(node) {
  if (!node || isEmpty(node.children)) return []
  return reduce(omit(node.children, "notheld"), (ids, child) => {
    if (!child || child.isFactor) return ids
    const modelProviderId = Object.values(child.values || {}).find(v => v?.modelProviderId)?.modelProviderId
    if (modelProviderId) return [...ids, modelProviderId]
    return [...ids, ...collectLeafModelProviderIds(child)]
  }, [])
}
